import { REFERENCE_CATEGORY_LABELS, type ReferenceCard as Card } from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { TARGET_ICON } from './Pickers.js';
import {
  absoluteTime,
  fileKindLabel,
  formatBytes,
  hostOf,
  personLabel,
  referenceIcon,
  relativeTime,
} from './format.js';

/**
 * WHAT A REFERENCE LOOKS LIKE IN THE LIBRARY.
 *
 * The picture is always the server's RE-ENCODED preview (owner decision R1),
 * never the uploaded original; without one, the kind of thing it is (a link, a
 * note, a PDF) is drawn as an icon. The title is the card's one button, stretched
 * over the whole card, so the card opens from anywhere and is still a single
 * focus stop; the ⋯ menu sits above that stretch.
 */
export function ReferenceThumb({ card, size }: { card: Card; size: 'card' | 'row' }) {
  const ready = card.preview.status === 'READY' && card.preview.thumbUrl !== null;
  return (
    <div className="ref-thumb" data-size={size} data-kind={card.sourceType} data-testid={`ref-card-thumb-${card.id}`}>
      {ready ? (
        <img src={card.preview.thumbUrl ?? ''} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="ref-thumb-icon" data-testid={`ref-card-icon-${card.id}`}>
          <Icon name={referenceIcon(card)} size={size === 'card' ? 30 : 18} strokeWidth={1.5} />
          {size === 'card' && card.file ? <span className="ref-thumb-kind">{fileKindLabel(card.file.kind)}</span> : null}
        </span>
      )}
      {ready && card.file?.kind === 'VIDEO' ? (
        <span className="ref-thumb-play" aria-hidden="true">
          <Icon name="play" size={size === 'card' ? 26 : 14} />
        </span>
      ) : null}
    </div>
  );
}

function metaLine(card: Card): string[] {
  const parts: string[] = [];
  if (card.file) {
    parts.push(fileKindLabel(card.file.kind));
    parts.push(formatBytes(card.file.byteSize));
  } else if (card.sourceType === 'LINK') {
    const domain = card.domain ?? (card.url ? hostOf(card.url) : null);
    if (domain) parts.push(domain);
  }
  return parts;
}

export function ReferenceCardView({
  card,
  view,
  selected,
  onOpen,
  menu,
}: {
  card: Card;
  view: 'grid' | 'list';
  selected: boolean;
  onOpen: () => void;
  menu: readonly MenuItem[];
}) {
  const firstLink = card.links[0] ?? null;
  const text = card.sourceType === 'NOTE' ? (card.excerpt ?? card.description) : (card.description ?? card.excerpt);
  const meta = metaLine(card);
  const who = personLabel(card.createdBy.displayName);
  const when = relativeTime(card.createdAt);

  return (
    <article
      className="ref-card"
      data-view={view}
      data-selected={selected ? '1' : undefined}
      data-testid={`ref-card-${card.id}`}
    >
      <ReferenceThumb card={card} size={view === 'grid' ? 'card' : 'row'} />
      <div className="ref-card-body">
        <div className="ref-card-head">
          <h3 className="ref-card-title">
            <button
              type="button"
              className="ref-card-open"
              aria-current={selected ? 'true' : undefined}
              data-testid={`ref-card-open-${card.id}`}
              onClick={onOpen}
            >
              {card.title}
            </button>
          </h3>
          {card.discussionCount !== null && card.discussionCount > 0 ? (
            <span
              className="ref-card-talk"
              title={`${String(card.discussionCount)} discussion${card.discussionCount === 1 ? '' : 's'}`}
              data-testid={`ref-card-discussions-${card.id}`}
            >
              <Icon name="chat" size={14} />
              <span>{card.discussionCount}</span>
              <span className="sr-only">{card.discussionCount === 1 ? 'discussion' : 'discussions'}</span>
            </span>
          ) : null}
          <div className="ref-card-menu">
            <MoreMenu items={menu} label={`Actions for ${card.title}`} testId={`ref-card-menu-${card.id}`} />
          </div>
        </div>

        <div className="ref-card-chips">
          {card.category !== null ? (
            <span className="ref-chip" data-tone="category" data-testid={`ref-card-category-${card.id}`}>
              {REFERENCE_CATEGORY_LABELS[card.category]}
            </span>
          ) : null}
          {view === 'grid'
            ? card.groups.slice(0, 2).map((g) => (
                <span key={g.id} className="ref-chip" data-tone="group" data-testid={`ref-card-group-${card.id}-${g.id}`}>
                  {g.name}
                </span>
              ))
            : null}
          {view === 'grid' && card.groups.length > 2 ? (
            <span className="ref-chip" data-tone="group">{`+${String(card.groups.length - 2)}`}</span>
          ) : null}
          {firstLink !== null ? (
            <span
              className="ref-chip"
              data-tone="link"
              data-removed={firstLink.state === 'REMOVED' ? '1' : undefined}
              data-testid={`ref-card-link-${card.id}`}
            >
              <Icon name={TARGET_ICON[firstLink.targetType]} size={12} />
              <span>{firstLink.label}</span>
              {card.links.length > 1 ? <span className="ref-chip-note">{`+${String(card.links.length - 1)}`}</span> : null}
            </span>
          ) : null}
        </div>

        {view === 'grid' && text ? <p className="ref-card-text">{text}</p> : null}

        <p className="ref-card-meta">
          {meta.map((m) => (
            <span key={m}>{m}</span>
          ))}
          <span>{who}</span>
          <time dateTime={card.createdAt} title={absoluteTime(card.createdAt)}>
            {when}
          </time>
        </p>
      </div>
    </article>
  );
}

/** Loading placeholders in the shape of the cards they stand for. */
export function ReferenceSkeletons({ view, count = 4 }: { view: 'grid' | 'list'; count?: number }) {
  return (
    <div className="ref-results" data-view={view} aria-hidden="true" data-testid="ref-skeletons">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="ref-card ref-skeleton" data-view={view}>
          <div className="ref-thumb" data-size={view === 'grid' ? 'card' : 'row'} />
          <div className="ref-card-body">
            <span className="ref-skeleton-line" style={{ width: '60%' }} />
            <span className="ref-skeleton-line" style={{ width: '35%' }} />
            {view === 'grid' ? <span className="ref-skeleton-line" style={{ width: '85%' }} /> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
